/**
 * Curriculum detail — shared presentational view of one syllabus's book Table of
 * Contents (units → chapters → sections, each optionally mapped to a canonical
 * node). Editing is injected via the optional `edit` callbacks (admin + teacher wire
 * them through CurriculumDetailEditor); when `edit` is omitted the view is READ-ONLY
 * (teacher syllabus browse) — no affordances, no authoring warnings.
 *
 * Header is a card: display shows Edit/Delete; Edit expands it inline (name/board/
 * subject/grade + Save) — no modal. Rows carry ✎ (edit) / ✕ (delete) icon actions.
 */
import { useMemo, useState, type ReactNode, type CSSProperties } from 'react';
import { DataTable, type ColumnDef, SearchFilterBar, type FilterDef, Button, Input, Select, FormField } from '../components';
import type { BookLevel, CanonModule, CurriculumInfo, CurriculumPlacement } from './types';
import { estimateFirstPassHours } from './types';
import { gradeBoardFit, formatGradeBoards, type GradeBoardFit } from './gradeBoards';
import './curriculum-view.css';

const BOOK_LEVELS: BookLevel[] = ['unit', 'chapter', 'section'];
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
type CanonLevel = 'module' | 'chapter' | 'topic';
type Row = { p: CurriculumPlacement; depth: number };

const iconBtn: CSSProperties = { background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: '2px 4px', color: 'var(--text-secondary)' };

export interface CurriculumDetailEdit {
  onSaveCurriculum: (draft: Partial<CurriculumInfo>) => void;
  onDeleteCurriculum: () => void;
  onNewUnit: () => void;
  onNewChapter: () => void;
  onNewChapterUnder: (unit: CurriculumPlacement) => void;
  onNewTopic: (chapter: CurriculumPlacement) => void;
  onEditRow: (p: CurriculumPlacement) => void;
  onDeleteRow: (p: CurriculumPlacement) => void;
}

interface Props {
  curriculum: CurriculumInfo;
  placements: CurriculumPlacement[];
  modules: CanonModule[];
  questionCountByPlacement?: Record<string, number>;
  questionCountsByModule?: Record<string, number>;
  /** Teacher-authored questions per placement, already scoped by the server to
   *  the caller (all teachers for admin, own only for a teacher). */
  teacherQuestionCountByPlacement?: Record<string, number>;
  /** Rendered at the top-left of the header (e.g. a router back link). */
  backSlot?: ReactNode;
  /** Present → editable. Omit → read-only. */
  edit?: CurriculumDetailEdit;
  /** Option lists for the inline header-edit form (required when `edit` is set). */
  editOptions?: { classes: string[]; subjects: string[]; targetExams: string[] };
  /** Rendered in the header — e.g. a "used by N batches" banner. */
  headerNote?: ReactNode;
  /** Grade × Board is an admin authoring aid: it reports whether a module's
   *  grade-per-board tuples agree with this ToC, and is fixed on the Modules
   *  page, which teachers cannot reach. Pass false to drop the column, its
   *  filter and the mismatch banner. Defaults to shown for admin. */
  showGradeBoards?: boolean;
  /** Rendered after the table — the editor puts its placement modal here. */
  children?: ReactNode;
}

export function CurriculumDetailView({
  curriculum, placements, modules,
  questionCountByPlacement = {}, questionCountsByModule = {},
  teacherQuestionCountByPlacement = {},
  backSlot, edit, editOptions, headerNote, children, showGradeBoards = true,
}: Props) {
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [editingHeader, setEditingHeader] = useState(false);
  const [hdr, setHdr] = useState<Partial<CurriculumInfo>>({});
  const readOnly = !edit;
  const opts = editOptions ?? { classes: [], subjects: [], targetExams: [] };

  // ── Canonical lookup (id → node, path, level) ──
  const { byId, levelOf, pathOf } = useMemo(() => {
    const byId = new Map(modules.map((m) => [m.id, m]));
    const levelOf = (m?: CanonModule): CanonLevel | undefined => (m ? (m.moduleLevel ?? (m.isTopModule ? 'module' : 'chapter')) : undefined);
    const pathOf = (m?: CanonModule): string => {
      if (!m) return '';
      const chain = [m.displayName]; let cur = m, hops = 0;
      while (cur.parentModuleId && hops < 3) {
        const p = byId.get(cur.parentModuleId); if (!p) break;
        chain.unshift(p.displayName); cur = p; hops++;
      }
      return chain.slice(0, -1).join(' › ');
    };
    return { byId, levelOf, pathOf };
  }, [modules]);

  // ── Book ToC tree → flat rows (parentId nesting, ordered by order/number) ──
  const rows = useMemo<Row[]>(() => {
    const byParent = new Map<string, CurriculumPlacement[]>();
    for (const p of placements) {
      const k = p.parentId ?? '__root__';
      (byParent.get(k) ?? byParent.set(k, []).get(k)!).push(p);
    }
    const sortNodes = (list: CurriculumPlacement[]) => list.sort((a, b) => {
      const ao = a.order ?? 1e9, bo = b.order ?? 1e9;
      if (ao !== bo) return ao - bo;
      return (a.number || '').localeCompare(b.number || '', undefined, { numeric: true });
    });
    const out: Row[] = [];
    const walk = (key: string, depth: number) => {
      for (const p of sortNodes(byParent.get(key) ?? [])) { out.push({ p, depth }); walk(p.id, depth + 1); }
    };
    walk('__root__', 0);
    return out;
  }, [placements]);

  // ── Counting conflicts (authoring aid) ──
  const countingConflicts = useMemo(() => {
    const byNode = new Map<string, CurriculumPlacement[]>();
    for (const p of placements) {
      for (const nodeId of p.canonicalNodeIds ?? []) {
        const arr = byNode.get(nodeId) ?? byNode.set(nodeId, []).get(nodeId)!;
        arr.push(p);
      }
    }
    const conflicts: { nodeId: string; nodeName: string; directCount: number; rows: CurriculumPlacement[] }[] = [];
    for (const [nodeId, ps] of byNode) {
      if (ps.length < 2) continue;
      const directCount = questionCountsByModule[nodeId] ?? 0;
      if (directCount <= 0) continue;
      conflicts.push({ nodeId, nodeName: byId.get(nodeId)?.displayName ?? nodeId, directCount, rows: ps });
    }
    return conflicts;
  }, [placements, questionCountsByModule, byId]);
  const conflictRowIds = useMemo(() => new Set(countingConflicts.flatMap((c) => c.rows.map((r) => r.id))), [countingConflicts]);

  // ── Unmapped canonical nodes that carry questions ("lost" questions) ──
  // Any subject canonical node (same grade/exam) with student-visible questions
  // that no book row maps to → those questions are unreachable via this
  // curriculum. Surfaced as a data-mapping warning above the filters.
  const unmappedWithQuestions = useMemo(() => {
    // A book node maps to the whole canonicalNodeIds set (many-to-one) — a module is
    // "reachable" if any placement lists it.
    const mapped = new Set(placements.flatMap((p) => p.canonicalNodeIds ?? []));
    type Unmapped = { id: string; name: string; level?: CanonLevel; path: string; count: number; grades?: string[] };
    const out: Unmapped[] = [];
    for (const [mid, count] of Object.entries(questionCountsByModule)) {
      if (count <= 0 || mapped.has(mid)) continue;
      const m = byId.get(mid);
      if (!m) continue; // only known canonical nodes
      // Nothing is excluded here any more. The counts arrive scoped to this
      // curriculum's OWN pool key, so every question in them is one a student of
      // this curriculum can see — and therefore one that needs a chapter. The old
      // exclusion ("gradeBoards says this board teaches the topic at another
      // grade, so the questions belong to that curriculum") made sense only while
      // the count was the wider grade+targetExam scope. Against a pool-keyed count
      // it suppresses a real contradiction: the question says cbse:11, the module
      // says CBSE teaches it at 10, and one of them is wrong. Across chemistry it
      // was hiding 160 genuinely unreachable questions.
      //
      // The disagreement is still surfaced — as an annotation on the row, so the
      // admin can see which fix applies — but it no longer removes the row.
      const fit = gradeBoardFit(m.gradeBoards, curriculum.board, curriculum.grade);
      out.push({ id: mid, name: m.displayName, level: levelOf(m), path: pathOf(m), count,
        ...(fit.kind === 'otherGrade' ? { grades: fit.grades } : {}) });
    }
    return out.sort((a, b) => b.count - a.count);
  }, [placements, questionCountsByModule, byId, levelOf, pathOf, curriculum.board, curriculum.grade]);
  const lostQuestionCount = useMemo(() => unmappedWithQuestions.reduce((s, u) => s + u.count, 0), [unmappedWithQuestions]);

  // ── Grade × Board per row (authoring aid) ──
  // A mapped node whose tuples put this board at a different grade is an authoring
  // inconsistency: the ToC says "this board teaches it at grade G", the module says
  // otherwise. Surfaced per-row (column) and enumerated in a banner.
  const fitByRow = useMemo(() => {
    const map = new Map<string, { primary?: CanonModule; fit: GradeBoardFit; anyMismatch: boolean }>();
    for (const p of placements) {
      const ids = p.canonicalNodeIds ?? [];
      const primary = ids[0] ? byId.get(ids[0]) : undefined;
      map.set(p.id, {
        primary,
        fit: gradeBoardFit(primary?.gradeBoards, curriculum.board, curriculum.grade),
        anyMismatch: ids.some((i) => gradeBoardFit(byId.get(i)?.gradeBoards, curriculum.board, curriculum.grade).kind === 'otherGrade'),
      });
    }
    return map;
  }, [placements, byId, curriculum.board, curriculum.grade]);

  const gradeBoardConflicts = useMemo(() => {
    const out: { row: CurriculumPlacement; nodeName: string; grades: string[] }[] = [];
    for (const p of placements) {
      for (const nodeId of p.canonicalNodeIds ?? []) {
        const m = byId.get(nodeId);
        if (!m) continue;
        const fit = gradeBoardFit(m.gradeBoards, curriculum.board, curriculum.grade);
        if (fit.kind === 'otherGrade') out.push({ row: p, nodeName: m.displayName, grades: fit.grades });
      }
    }
    return out;
  }, [placements, byId, curriculum.board, curriculum.grade]);

  const topLevel = useMemo(() => placements.filter((p) => !p.parentId), [placements]);
  const chapterCount = useMemo(() => placements.filter((p) => p.bookLevel === 'chapter').length, [placements]);

  const visibleRows = useMemo(() => {
    let r = rows;
    if (filters.bookLevel) r = r.filter((x) => x.p.bookLevel === filters.bookLevel);
    if (filters.mapping === 'unmapped') r = r.filter((x) => !x.p.canonicalNodeIds?.length);
    else if (filters.mapping) r = r.filter((x) => { const n = x.p.canonicalNodeIds?.[0]; return n && levelOf(byId.get(n)) === filters.mapping; });
    if (filters.gradeBoard) r = r.filter((x) => {
      const f = fitByRow.get(x.p.id);
      if (!f) return false;
      if (filters.gradeBoard === 'mismatch') return f.anyMismatch;
      const has = !!f.primary?.gradeBoards?.length;
      return filters.gradeBoard === 'set' ? has : !!f.primary && !has;
    });
    const q = (filters.search || '').trim().toLowerCase();
    if (q) r = r.filter((x) => x.p.displayName.toLowerCase().includes(q) || (x.p.number || '').includes(q));
    return r;
  }, [rows, filters, byId, levelOf, fitByRow]);

  const filterDefs: FilterDef[] = [
    { key: 'bookLevel', placeholder: 'All book levels', options: BOOK_LEVELS.map((b) => ({ value: b, label: cap(b) })) },
    { key: 'mapping', placeholder: 'All mappings', options: [
      { value: 'chapter', label: 'Maps to chapter' },
      { value: 'topic', label: 'Maps to topic' },
      { value: 'unmapped', label: 'Unmapped' },
    ] },
    ...(showGradeBoards ? [{ key: 'gradeBoard', placeholder: 'All grade × board', options: [
      { value: 'mismatch', label: 'Grade × Board mismatch' },
      { value: 'set', label: 'Has Grade × Board' },
      { value: 'unset', label: 'No Grade × Board' },
    ] }] : []),
  ];

  const allColumns: ColumnDef<Row>[] = [
    { key: 'number', header: 'No.', mobile: 'hide', render: (r) => <strong>{r.p.number}</strong> },
    {
      // Bounded and wrapping. Unbounded, a long chapter title held the column on
      // one line and pushed the row ACTIONS off the right edge behind the
      // table's horizontal scroll — where nobody can guess an edit button is.
      key: 'name', header: 'Unit/Chapter/Section', mobile: 'title',
      render: (r) => (
        <span style={{
          display: 'inline-block',
          paddingLeft: r.depth * 22,
          fontWeight: r.depth === 0 ? 600 : 400,
          maxWidth: 260,
          whiteSpace: 'normal',
          overflowWrap: 'anywhere',
        }}>{r.p.displayName}</span>
      ),
      // Cards can't carry the table's indentation, so the number prefix is what
      // conveys where a row sits in the book ("2.3 Refraction").
      mobileRender: (r) => `${r.p.number} ${r.p.displayName}`,
    },
    { key: 'bookLevel', header: 'Book level', mobile: 'accessory', render: (r) => r.p.bookLevel },
    {
      key: 'canonical', header: 'Canonical node', mobileLabel: 'Mapped to',
      render: (r) => {
        const ids = r.p.canonicalNodeIds ?? [];
        if (!ids.length) return <span style={{ color: 'var(--text-secondary)' }}>— not mapped —</span>;
        const node = byId.get(ids[0]);
        if (!node) return <span style={{ color: 'var(--color-error)' }}>missing node</span>;
        return (
          <div style={{ maxWidth: 240, overflowWrap: 'anywhere' }}>
            <div>{node.displayName}{ids.length > 1 && <span style={{ color: 'var(--text-secondary)' }}> +{ids.length - 1}</span>}</div>
            {pathOf(node) && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{pathOf(node)}</div>}
          </div>
        );
      },
    },
    { key: 'level', header: 'Level', mobile: 'hide', render: (r) => { const n = r.p.canonicalNodeIds?.[0]; return n ? (levelOf(byId.get(n)) ?? '—') : '—'; } },
    {
      // Effort inputs, on the row rather than only inside the edit modal — the
      // question this column answers is "which chapters are still unsized?", and
      // that cannot be answered by opening thirty modals one at a time.
      key: 'effort', header: 'Effort', mobile: 'hide',
      render: (r) => {
        // Units and sections carry no effort of their own: a unit's is the sum of
        // its chapters, and asking at both levels would double-count.
        if (r.p.bookLevel !== 'chapter') return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
        if (!r.p.pageCount) {
          return <span style={{ color: 'var(--text-tertiary)' }} title="No page count — this chapter cannot be sized, and a schedule will say so rather than assume it is free">not sized</span>;
        }
        const cx = r.p.complexityFactor ?? 'medium';
        const hours = estimateFirstPassHours(r.p.pageCount, r.p.complexityFactor);
        // One compact line. This table is already wide enough that the row
        // ACTIONS sit off the right edge behind a horizontal scroll, so an
        // informational column must not cost more width than it has to.
        return (
          <span title={`${cx.replace('-', ' ')} — ${hours} h for a first read`} style={{ whiteSpace: 'nowrap' }}>
            {r.p.pageCount}pp · {hours}h
          </span>
        );
      },
    },
    {
      // What the mapped node itself says about grade-per-board. Authored on the module
      // (Modules → Grade × Board), read-only here — it decides which grade-curriculum a
      // topic's questions belong to, so a mismatch with this ToC is worth seeing.
      key: 'gradeBoards', header: 'Grade × Board', mobile: 'hide',
      render: (r) => {
        const f = fitByRow.get(r.p.id);
        if (!f?.primary) return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
        const tuples = formatGradeBoards(f.primary.gradeBoards);
        if (!tuples) return <span style={{ color: 'var(--text-tertiary)' }} title="No tuple — grade falls back to the question's class.">—</span>;
        const mismatch = f.fit.kind === 'otherGrade';
        return (
          <span
            style={{ fontSize: 11, color: mismatch ? 'var(--color-warning)' : 'var(--text-secondary)' }}
            title={mismatch
              ? `${curriculum.board} teaches this topic at Class ${f.fit.grades.join(' / ')}, not Class ${curriculum.grade} — its questions belong to that curriculum.`
              : tuples}
          >
            {mismatch && '⚠ '}{tuples}
          </span>
        );
      },
    },
    {
      // Teacher-authored questions never carry a board-grade pool key, so they
      // are absent from the library count beside this one — a curriculum can
      // read 0 questions while the teacher has authored dozens against it.
      key: 'teacherQuestions', header: 'Teacher Questions', mobile: 'hide',
      render: (r) => {
        const n = teacherQuestionCountByPlacement[r.p.id] ?? 0;
        return <span className="dc-count" style={{ color: n ? undefined : 'var(--text-tertiary)' }}>{n}</span>;
      },
    },
    {
      key: 'questions', header: 'Questions',
      render: (r) => {
        const n = questionCountByPlacement[r.p.id] ?? 0;
        const conflicted = !readOnly && conflictRowIds.has(r.p.id);
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: n ? undefined : 'var(--text-secondary)', fontWeight: r.depth === 0 ? 600 : 400 }}>
            {n}
            {conflicted && <span title="This row shares its canonical node (with directly-tagged questions) with another row — those questions are double-counted here." style={{ color: 'var(--color-warning)', cursor: 'help' }}>⚠</span>}
          </span>
        );
      },
    },
    ...(edit ? [{
      key: 'actions', header: '', mobile: 'accessory',
      render: (r: Row) => (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
          {r.p.bookLevel === 'unit' && <Button variant="secondary" size="sm" onClick={() => edit.onNewChapterUnder(r.p)}>+ Chapter</Button>}
          {r.p.bookLevel === 'chapter' && <Button variant="secondary" size="sm" onClick={() => edit.onNewTopic(r.p)}>+ Topic</Button>}
          <button type="button" title="Edit" style={iconBtn} onClick={() => edit.onEditRow(r.p)}>✎</button>
          <button type="button" title="Delete" style={{ ...iconBtn, color: 'var(--color-error)' }} onClick={() => edit.onDeleteRow(r.p)}>✕</button>
        </div>
      ),
    } as ColumnDef<Row>] : []),
  ];
  // Split from the literal above so its annotation still types the render args.
  const columns = allColumns.filter((c) => showGradeBoards || c.key !== 'gradeBoards');

  const description = (
    <>
      {curriculum.board}{curriculum.grade ? ` · Class ${curriculum.grade}` : ''} · {cap(curriculum.subject)} —
      {topLevel.some((p) => p.bookLevel === 'unit') ? ` ${topLevel.filter((p) => p.bookLevel === 'unit').length} units ·` : ''}
      {' '}{chapterCount} chapters · {placements.length} entries. Left = the curriculum’s index; right = the canonical node each maps to (optional).
    </>
  );

  const startEdit = () => {
    // Carry examTrack + isActive through the draft so saving the header (a full
    // replace on the backend) doesn't reset them to their defaults.
    setHdr({
      id: curriculum.id, name: curriculum.name, board: curriculum.board,
      subject: curriculum.subject, grade: curriculum.grade,
      examTrack: curriculum.examTrack, isActive: curriculum.isActive ?? true,
    });
    setEditingHeader(true);
  };
  const saveHeader = () => { edit?.onSaveCurriculum(hdr); setEditingHeader(false); };
  // One-click active/inactive toggle. Sends the whole curriculum (save is a full
  // replace on the backend) with only isActive flipped, so nothing else resets.
  const toggleActive = () => {
    edit?.onSaveCurriculum({ ...curriculum, isActive: !(curriculum.isActive ?? true) });
  };

  return (
    <div className="settings-section">
      {/* ── Header block ── */}
      {readOnly ? (
        <div className="section-header">
          <div className="section-header-top" style={{ alignItems: 'flex-start', gap: 12 }}>
            <div>
              {backSlot}
              <h2 style={{ margin: '4px 0 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                {curriculum.name}
                {curriculum.isActive === false && (
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-error)', border: '1px solid var(--color-error)', borderRadius: 999, padding: '1px 8px' }}>Inactive</span>
                )}
              </h2>
              <p className="section-description" style={{ marginTop: 4 }}>{description}</p>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ border: '1px solid var(--border-color, #e2e8f0)', borderRadius: 8, padding: '12px 14px', marginBottom: 12 }}>
          {backSlot}
          {headerNote}
          {!editingHeader ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div>
                <h2 style={{ margin: '4px 0 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                {curriculum.name}
                {curriculum.isActive === false && (
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-error)', border: '1px solid var(--color-error)', borderRadius: 999, padding: '1px 8px' }}>Inactive</span>
                )}
              </h2>
                <p className="section-description" style={{ marginTop: 4 }}>{description}</p>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <Button
                  variant={curriculum.isActive === false ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={toggleActive}
                >
                  {curriculum.isActive === false ? 'Activate' : 'Deactivate'}
                </Button>
                <Button variant="secondary" size="sm" onClick={startEdit}>Edit</Button>
                <Button variant="danger" size="sm" onClick={edit!.onDeleteCurriculum}>Delete</Button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 12, maxWidth: 640, marginTop: 4 }}>
              <FormField label="Name">
                <Input value={hdr.name ?? ''} onChange={(e) => setHdr({ ...hdr, name: e.target.value })} />
              </FormField>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <FormField label="Board / exam">
                  <Select value={hdr.board ?? ''} onChange={(e) => setHdr({ ...hdr, board: e.target.value || undefined })}>
                    {opts.targetExams.map((t) => <option key={t} value={t}>{t}</option>)}
                  </Select>
                </FormField>
                <FormField label="Subject">
                  <Select value={hdr.subject ?? ''} onChange={(e) => setHdr({ ...hdr, subject: e.target.value || undefined })}>
                    {opts.subjects.map((s) => <option key={s} value={s}>{cap(s)}</option>)}
                  </Select>
                </FormField>
                <FormField label="Grade">
                  <Select value={hdr.grade ?? ''} onChange={(e) => setHdr({ ...hdr, grade: e.target.value || undefined })}>
                    <option value="">— none —</option>
                    {opts.classes.map((c) => <option key={c} value={c}>Class {c}</option>)}
                  </Select>
                </FormField>
              </div>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={hdr.isActive ?? true}
                  onChange={(e) => setHdr({ ...hdr, isActive: e.target.checked })}
                />
                Active (inactive curricula are hidden from the plan blueprint &amp; exam-goal decomposition)
              </label>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Button variant="secondary" size="sm" onClick={() => setEditingHeader(false)}>Cancel</Button>
                <Button variant="primary" size="sm" disabled={!hdr.name || !hdr.board || !hdr.subject} onClick={saveHeader}>Save</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {unmappedWithQuestions.length > 0 && (
        <div style={{
          border: '1px solid var(--color-error)', borderRadius: 8,
          background: 'color-mix(in srgb, var(--color-error) 10%, transparent)',
          padding: '12px 14px', marginBottom: 12, fontSize: 13,
        }}>
          <strong>⚠ {unmappedWithQuestions.length} canonical {unmappedWithQuestions.length === 1 ? 'node has' : 'nodes have'} questions but {unmappedWithQuestions.length === 1 ? 'is' : 'are'} not mapped in this curriculum</strong>
          {' '}— {lostQuestionCount} question{lostQuestionCount === 1 ? '' : 's'} for {curriculum.board}{curriculum.grade ? ` · Class ${curriculum.grade}` : ''} won't be reachable by students here. Map them to a chapter/section to recover them.
          <ul style={{ margin: '8px 0 0', paddingLeft: 20, maxHeight: 180, overflowY: 'auto' }}>
            {unmappedWithQuestions.map((u) => (
              <li key={u.id} style={{ marginBottom: 2 }}>
                <strong>{u.name}</strong>
                {u.path ? <span style={{ color: 'var(--text-secondary)' }}> — {u.path}</span> : null}
                {u.level ? <span style={{ color: 'var(--text-tertiary)' }}> · {u.level}</span> : null}
                <span> · {u.count} question{u.count === 1 ? '' : 's'}</span>
                {u.grades?.length ? (
                  // The question's pool key and the module's Grade × Board disagree.
                  // Both cannot be right, and which to fix is the admin's call:
                  // map it here, or correct whichever record is wrong.
                  <span style={{ color: 'var(--color-warning)' }}>
                    {' '}· but Grade × Board says {curriculum.board} teaches this at Class {u.grades.join(' / ')}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="cv-list-toolbar">
        <div className="cv-list-toolbar-filters">
          <SearchFilterBar filters={filterDefs} values={filters} onChange={setFilters} searchPlaceholder="Search number / title…" hideFetch />
        </div>
        {edit && <Button variant="secondary" size="sm" onClick={edit.onNewUnit}>+ Add unit</Button>}
        {edit && <Button variant="primary" size="sm" onClick={edit.onNewChapter}>+ Add chapter</Button>}
      </div>

      {showGradeBoards && !readOnly && gradeBoardConflicts.length > 0 && (
        <div style={{
          border: '1px solid var(--color-warning)', borderRadius: 6,
          background: 'color-mix(in srgb, var(--color-warning) 12%, transparent)',
          padding: '10px 12px', marginBottom: 10, fontSize: 13,
        }}>
          <strong>⚠ {gradeBoardConflicts.length} Grade × Board {gradeBoardConflicts.length === 1 ? 'mismatch' : 'mismatches'}.</strong>{' '}
          This ToC maps {gradeBoardConflicts.length === 1 ? 'a node' : 'nodes'} that {curriculum.board} teaches at a different grade — either the
          mapping is wrong, or the module's Grade × Board needs a Class {curriculum.grade} row for {curriculum.board} (edit it on the Modules page):
          <ul style={{ margin: '6px 0 0', paddingLeft: 20, maxHeight: 160, overflowY: 'auto' }}>
            {gradeBoardConflicts.map((c, i) => (
              <li key={`${c.row.id}-${i}`}>
                <strong>{c.nodeName}</strong> — mapped by {c.row.number} {c.row.displayName}, but {curriculum.board} teaches it at Class {c.grades.join(' / ')}
              </li>
            ))}
          </ul>
        </div>
      )}

      {!readOnly && countingConflicts.length > 0 && (
        <div style={{
          border: '1px solid var(--color-warning)', borderRadius: 6,
          background: 'color-mix(in srgb, var(--color-warning) 12%, transparent)',
          padding: '10px 12px', marginBottom: 10, fontSize: 13,
        }}>
          <strong>⚠ {countingConflicts.length} question-count {countingConflicts.length === 1 ? 'conflict' : 'conflicts'}.</strong>{' '}
          These canonical nodes carry directly-tagged questions but are mapped by more than one book row, so those questions are counted in every mapped row:
          <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
            {countingConflicts.map((c) => (
              <li key={c.nodeId}>
                <strong>{c.nodeName}</strong> — {c.directCount} direct question{c.directCount === 1 ? '' : 's'}, counted in {c.rows.length} rows:{' '}
                {c.rows.map((r) => `${r.number} ${r.displayName}`).join(', ')}
              </li>
            ))}
          </ul>
        </div>
      )}

      <DataTable columns={columns} data={visibleRows} rowKey={(r) => r.p.id}
        rowClassName={(r) => (r.p.bookLevel === 'unit' || r.p.bookLevel === 'chapter' ? 'curr-chapter-row' : '')}
        emptyMessage="No entries match." compact bordered mobileCards />

      {children}
    </div>
  );
}
